import { adminPath, isNotFoundError, type AdminClient } from '../core/index.js'
import { workerLoginPath, type WorkerLoginApi } from './components/workerLoginApi.js'
import type {
  AdminLoginResult,
  AdminUser,
  AdminUserWithPassword,
  CreateAdminInput,
  DemoPeople,
  DemoSession,
  DemoSessionResponse,
  FacilityAdministrator,
  Invitation,
  InvitationResult,
  InviteFacilityAdministratorInput,
  InviteOperatorInput,
  OpenDemoSessionInput,
  SignInEvent,
  SignInUser,
  UpdateAdminInput,
  WorkerLogin,
} from './types.js'

export interface AdminOperator {
  operatorId: string
  clientId: string
  email: string
  firstName: string
  lastName: string
  createdAt: string
  /** `pending` while the operator's invitation waits (invitations plan D2); absent on a login from before, which reads as accepted. */
  invitationStatus?: 'pending' | 'accepted'
}

export interface OperatorUpdate {
  email?: string
  firstName?: string
  lastName?: string
  rotatePassword?: boolean
}

/** The accounts module's admin calls: the admin area's own sign-in and logins, a client's operators, facility administrators and worker logins, the sign-in history and demo sessions. */
export interface AccountsAdminApi {
  /** The admin area's sign-in: proves the credential and says who it belongs to. */
  login(): Promise<AdminLoginResult>
  /** Every admin login, the root first. Root only. */
  listAdmins(): Promise<AdminUser[]>
  /** Creates an admin with a one-time password. Root only. */
  createAdmin(input: CreateAdminInput): Promise<AdminUserWithPassword>
  /** Replaces an admin's password with a new one-time password. Root only. */
  rotateAdminPassword(username: string): Promise<AdminUserWithPassword>
  /** Sets an admin's name, the root's included. Root only. */
  updateAdmin(username: string, input: UpdateAdminInput): Promise<AdminUser>
  /** Removes an admin login. Root only. */
  removeAdmin(username: string): Promise<void>
  /** Invites an admin or sales user (invitations plan phase 1): the login is made at once, switched off, and the person is emailed a link and a code. Root only. */
  inviteAdmin(input: CreateAdminInput): Promise<InvitationResult>
  /** The platform's own invitations, newest first. Root only. */
  listAdminInvitations(): Promise<Invitation[]>
  resendAdminInvitation(invitationId: string): Promise<InvitationResult>
  revokeAdminInvitation(invitationId: string): Promise<Invitation | null>
  /** Every pending invitation on the platform, by kind and client (System › Invitations). */
  listPendingInvitations(): Promise<Invitation[]>
  /** Root only: everyone with a sign-in on record, latest first. */
  listSignInUsers(): Promise<SignInUser[]>
  /** Root only: one person's sign-ins, newest first. */
  listSignInEvents(userKey: string): Promise<SignInEvent[]>
  listOperators(registrationId: string): Promise<AdminOperator[]>
  createOperator(registrationId: string, input: { email: string; firstName: string; lastName: string }): Promise<AdminOperator & { password: string }>
  /** `password` is present only when `rotatePassword` was asked for - it is the new one, returned once. */
  updateOperator(registrationId: string, operatorId: string, input: OperatorUpdate): Promise<AdminOperator & { password?: string }>
  deleteOperator(registrationId: string, operatorId: string): Promise<void>
  /** A single-use link (valid one hour) the operator can set their own password with. */
  createOperatorResetLink(registrationId: string, operatorId: string): Promise<{ resetUrl: string; expiresInSeconds: number }>
  /** Every facility administrator under the client - each names the facilities they manage in `facilityIds`. */
  listFacilityAdministrators(clientId: string): Promise<FacilityAdministrator[]>
  /** `password` is generated by the API and returned only here. */
  createFacilityAdministrator(clientId: string, input: { email: string; firstName: string; lastName: string; facilityIds: string[]; phone?: string }): Promise<FacilityAdministrator & { password: string }>
  /** Invites an operator (invitations plan phase 2): the login is made at once, switched off, and the person is emailed a link and a code. */
  inviteOperator(clientId: string, input: InviteOperatorInput): Promise<InvitationResult>
  listOperatorInvitations(clientId: string): Promise<Invitation[]>
  resendOperatorInvitation(clientId: string, invitationId: string): Promise<InvitationResult>
  revokeOperatorInvitation(clientId: string, invitationId: string): Promise<Invitation | null>
  /** Invites a facility administrator to named facilities. */
  inviteFacilityAdministrator(clientId: string, input: InviteFacilityAdministratorInput): Promise<InvitationResult>
  listFacilityAdministratorInvitations(clientId: string): Promise<Invitation[]>
  resendFacilityAdministratorInvitation(clientId: string, invitationId: string): Promise<InvitationResult>
  revokeFacilityAdministratorInvitation(clientId: string, invitationId: string): Promise<Invitation | null>
  /** A worker's own sign-in: read, create (password once), change, remove, reset link. */
  workerLogin(clientId: string, facilityId: string, workerId: string): WorkerLoginApi
  /** Opens a demo client as one of its people, without a password; refused for a real client. */
  openDemoSession(clientId: string, input: OpenDemoSessionInput): Promise<DemoSessionResponse>
  /** Everyone a demo client can be opened as, grouped for the chooser. Staff and the list of facilities only; the workers come a facility at a time. */
  listDemoPeople(clientId: string): Promise<DemoPeople>
  /** One facility's workers, for the chooser to show when that facility is opened. */
  listDemoWorkers(clientId: string, facilityId: string): Promise<DemoPeople>
  /** The client's workers whose name or id holds the text, grouped by facility. */
  searchDemoWorkers(clientId: string, query: string): Promise<DemoPeople>
  /** Who has opened this demo client as whom, newest first. */
  listDemoSessions(clientId: string): Promise<DemoSession[]>
}

export function createAccountsAdminApi(client: AdminClient): AccountsAdminApi {
  const operatorPath = (registrationId: string, operatorId: string): string => `${adminPath.registration(registrationId)}/operators/${encodeURIComponent(operatorId)}`
  const demoPeoplePath = (clientId: string): string => `${adminPath.client(clientId)}/demo-sessions/people`
  return {
    login: () => client.call('POST', '/admin/login'),
    listAdmins: () => client.call('GET', '/admin/admins'),
    createAdmin: (input) => client.call('POST', '/admin/admins', input),
    rotateAdminPassword: (username) => client.call('POST', `/admin/admins/${encodeURIComponent(username)}/password`),
    updateAdmin: (username, input) => client.call('PATCH', `/admin/admins/${encodeURIComponent(username)}`, input),
    removeAdmin: (username) => client.remove(`/admin/admins/${encodeURIComponent(username)}`),
    inviteAdmin: (input) => client.call('POST', '/admin/admins/invitations', input),
    listAdminInvitations: () => client.call('GET', '/admin/admins/invitations'),
    resendAdminInvitation: (invitationId) => client.call('POST', `/admin/admins/invitations/${encodeURIComponent(invitationId)}/resend`),
    revokeAdminInvitation: (invitationId) => client.call('POST', `/admin/admins/invitations/${encodeURIComponent(invitationId)}/revoke`),
    listPendingInvitations: () => client.call('GET', '/admin/invitations'),
    listSignInUsers: () => client.call('GET', '/admin/sign-ins'),
    listSignInEvents: (userKey) => client.call('GET', `/admin/sign-ins/events?user=${encodeURIComponent(userKey)}`),
    listOperators: (registrationId) => client.call('GET', `${adminPath.registration(registrationId)}/operators`),
    createOperator: (registrationId, input) => client.call('POST', `${adminPath.registration(registrationId)}/operators`, input),
    updateOperator: (registrationId, operatorId, input) => client.call('PATCH', operatorPath(registrationId, operatorId), input),
    deleteOperator: (registrationId, operatorId) => client.remove(operatorPath(registrationId, operatorId)),
    createOperatorResetLink: (registrationId, operatorId) => client.call('POST', `${operatorPath(registrationId, operatorId)}/reset-link`),
    listFacilityAdministrators: (clientId) => client.call('GET', `${adminPath.client(clientId)}/facility-administrators`),
    createFacilityAdministrator: (clientId, input) => client.call('POST', `${adminPath.client(clientId)}/facility-administrators`, input),
    inviteOperator: (clientId, input) => client.call('POST', `${adminPath.client(clientId)}/operators/invitations`, input),
    listOperatorInvitations: (clientId) => client.call('GET', `${adminPath.client(clientId)}/operators/invitations`),
    resendOperatorInvitation: (clientId, invitationId) => client.call('POST', `${adminPath.client(clientId)}/operators/invitations/${encodeURIComponent(invitationId)}/resend`),
    revokeOperatorInvitation: (clientId, invitationId) => client.call('POST', `${adminPath.client(clientId)}/operators/invitations/${encodeURIComponent(invitationId)}/revoke`),
    inviteFacilityAdministrator: (clientId, input) => client.call('POST', `${adminPath.client(clientId)}/facility-administrators/invitations`, input),
    listFacilityAdministratorInvitations: (clientId) => client.call('GET', `${adminPath.client(clientId)}/facility-administrators/invitations`),
    resendFacilityAdministratorInvitation: (clientId, invitationId) => client.call('POST', `${adminPath.client(clientId)}/facility-administrators/invitations/${encodeURIComponent(invitationId)}/resend`),
    revokeFacilityAdministratorInvitation: (clientId, invitationId) => client.call('POST', `${adminPath.client(clientId)}/facility-administrators/invitations/${encodeURIComponent(invitationId)}/revoke`),
    workerLogin: (clientId, facilityId, workerId) => {
      const path = workerLoginPath(clientId, facilityId, workerId)
      return {
        get: async () => {
          try {
            return await client.call<WorkerLogin>('GET', path)
          } catch (error) {
            if (isNotFoundError(error)) {
              return null
            }
            throw error
          }
        },
        create: (input) => client.call('POST', path, input),
        update: (input) => client.call('PATCH', path, input),
        remove: () => client.remove(path),
        resetLink: () => client.call('POST', `${path}/reset-link`),
        invitation: () => client.call('GET', `${path}/invitation`),
        invite: (input) => client.call('POST', `${path}/invitation`, input),
        resendInvitation: () => client.call('POST', `${path}/invitation/resend`),
        revokeInvitation: () => client.call('POST', `${path}/invitation/revoke`),
      }
    },
    openDemoSession: (clientId, input) => client.call('POST', `${adminPath.client(clientId)}/demo-sessions`, input),
    listDemoPeople: (clientId) => client.call('GET', `${demoPeoplePath(clientId)}?workers=none`),
    listDemoWorkers: (clientId, facilityId) => client.call('GET', `${demoPeoplePath(clientId)}?facilityId=${encodeURIComponent(facilityId)}`),
    searchDemoWorkers: (clientId, query) => client.call('GET', `${demoPeoplePath(clientId)}?q=${encodeURIComponent(query)}`),
    listDemoSessions: (clientId) => client.call('GET', `${adminPath.client(clientId)}/demo-sessions`),
  }
}
